import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconEditAdmin from '../../assets/edit_admin.svg';
import AppSpinner from '../../components/AppSpinner';

interface RegistrationData {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  };
  status: string;
  createdAt: string;
}

const AdminCounselorRegistrationListPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [registrations, setRegistrations] = useState<RegistrationData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [searchStatus, setSearchStatus] = useState<string>('all');
  const [keyword, setKeyword] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchRegistrations();
  }, [isAuthenticated, searchStatus, currentPage]);

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/admin/counselor-registrations', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          status: searchStatus,
          keyword,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setRegistrations(response.data.registrations);
    } catch (err) {
      setError('목록을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchRegistrations();
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchStatus(e.target.value);
    setCurrentPage(1);
  };

  if (loading && registrations.length === 0) return <AppSpinner fullscreen tip="Loading..." />;
  if (error) return <div className="admin-page">에러: {error}</div>;

  return (
    <div className="admin-page">
      <h1>상담사 등록 신청 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>상태</label>
          <select value={searchStatus} onChange={handleStatusChange}>
            <option value="all">전체</option>
            <option value="pending">대기중</option>
            <option value="approved">승인됨</option>
            <option value="rejected">거절됨</option>
          </select>
        </div>

        <div className="filter-group">
          <label>검색</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="이름, 아이디, 이메일 검색"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
        </div>
      </div>

      <div className="table-scroll">
        <table className="table user-list-table">
          <thead>
            <tr>
              <th>신청자</th>
              <th>아이디</th>
              <th>이메일</th>
              <th>신청일</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {registrations.length > 0 ? (
              registrations.map(reg => (
                <tr key={reg._id}>
                  <td>{reg.user?.displayName || '알 수 없음'}</td>
                  <td>{reg.user?.userId || '-'}</td>
                  <td>{reg.user?.email || '-'}</td>
                  <td>{new Date(reg.createdAt).toLocaleDateString()}</td>
                  <td>
                    <span
                      className={`status-badge ${
                        reg.status === 'approved'
                          ? 'normal'
                          : reg.status === 'rejected'
                            ? 'suspended'
                            : 'pending'
                      }`}
                    >
                      {reg.status === 'approved'
                        ? '승인'
                        : reg.status === 'rejected'
                          ? '거절'
                          : '대기'}
                    </span>
                  </td>
                  <td>
                    <img
                      onClick={() => navigate(`/admin/counselor-registrations/${reg._id}`)}
                      src={iconEditAdmin}
                      alt="상세보기"
                      className="adminEditIcon"
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="no-data">
                  신청 내역이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCounselorRegistrationListPage;
